import { X, Download, FileText, FileImage, FileVideo, Eye } from "lucide-react";
import type { MaterialData } from "../../../../lib/api";
import { ResultStep as LessonResult } from "../CreateLessonAI/steps/ResultStep";
import { ResultStep as SlidesResult } from "../CreateSlidesAI/steps/ResultStep";
import { PreviewStep as EvaluationPreview } from "../EvaluationCreator/steps/PreviewStep";
import { EVALUATION_TYPES } from "../EvaluationCreator/constants";
import { useState } from "react";

interface FileViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: {
    id: string;
    name: string;
    type: string;
    material: MaterialData;
  } | null;
  onDownload: (file: any) => void;
}

export default function FileViewerModal({
  isOpen,
  onClose,
  file,
  onDownload,
}: FileViewerModalProps) {
  const [selectedSlide, setSelectedSlide] = useState(0);

  if (!isOpen || !file) return null;

  const m = file.material;
  const meta = typeof m.content === "string" ? JSON.parse(m.content) : null;

  const renderContent = () => {
    if (!meta)
      return (
        <div className="p-8 text-center">
          Formato de arquivo não suportado para visualização.
        </div>
      );

    if (meta.kind === "ai_lesson_plan") {
      return (
        <div className="bg-(--bg-secondary) p-6 rounded-xl">
          <LessonResult
            generatedLesson={meta.data}
            onSave={() => {}}
            onCopy={() => {
              navigator.clipboard.writeText(JSON.stringify(meta.data, null, 2));
              alert("Copiado!");
            }}
            onDownload={() => onDownload(file)}
            onRegenerate={() => {}}
          />
        </div>
      );
    }

    if (meta.kind === "ai_slides") {
      return (
        <div className="bg-(--bg-secondary) p-6 rounded-xl">
          <SlidesResult
            generatedSlides={meta.data}
            slideStyle={meta.data.slideStyle || "modern"}
            colorScheme={meta.data.colorScheme || "blue"}
            selectedSlide={selectedSlide}
            onSelectSlide={setSelectedSlide}
            onSave={() => {}}
            onDownload={() => onDownload(file)}
            onRegenerate={() => {}}
          />
        </div>
      );
    }

    if (meta.kind === "ai_evaluation") {
      return (
        <div className="bg-(--bg-secondary) p-6 rounded-xl overflow-hidden">
          <EvaluationPreview
            formData={meta.formData}
            generatedContent={meta.data}
            userData={null}
            evaluationTypes={EVALUATION_TYPES}
          />
        </div>
      );
    }

    if (meta.kind === "uploaded_file" && meta.base64) {
      if (file.type === "image") {
        return (
          <div className="flex items-center justify-center p-8 bg-(--bg-secondary) rounded-xl">
            <img
              src={meta.base64}
              alt={file.name}
              className="max-w-full max-h-[70vh] rounded-lg shadow-lg"
            />
          </div>
        );
      }

      if (file.type === "pdf") {
        return (
          <div className="w-full h-[70vh] bg-(--bg-secondary) rounded-xl overflow-hidden shadow-lg">
            <iframe
              src={`${meta.base64}#toolbar=0`}
              className="w-full h-full border-none"
              title={file.name}
            />
          </div>
        );
      }

      return (
        <div className="p-12 text-center bg-(--bg-secondary) rounded-xl">
          <FileText className="w-16 h-16 mx-auto mb-4 text-(--text-tertiary)" />
          <h4 className="text-lg font-medium text-(--text-primary) mb-2">
            Visualização não disponível
          </h4>
          <p className="text-sm text-(--text-tertiary) mb-6">
            Este tipo de arquivo não pode ser visualizado diretamente no
            navegador.
          </p>
          <button
            onClick={() => onDownload(file)}
            className="flex items-center gap-2 px-6 py-3 bg-(--primary-color) text-white rounded-xl mx-auto font-medium transition hover:shadow-lg"
          >
            <Download className="w-5 h-5" />
            Baixar para ver
          </button>
        </div>
      );
    }

    return (
      <div className="p-8 text-center">
        Visualização não suportada para este item.
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-4 sm:p-6 backdrop-blur-md bg-black/40">
      <div className="bg-(--modal-bg) rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300 border border-(--border-color)">
        <div className="p-6 border-b border-(--border-color) flex items-center justify-between bg-(--bg-secondary)">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-(--primary-color)/10 rounded-lg">
              <Eye className="w-6 h-6 text-(--primary-color)" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-(--text-primary)">
                {file.name}
              </h2>
              <p className="text-sm text-(--text-tertiary)">
                {" "}
                Visualizando arquivo
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDownload(file)}
              className="p-2.5 bg-(--card-bg) border border-(--border-color) hover:bg-(--bg-accent) rounded-xl transition text-(--text-primary) shadow-sm"
              title="Baixar Arquivo"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2.5 bg-(--card-bg) border border-(--border-color) hover:bg-(--bg-accent) rounded-xl transition text-(--text-primary) shadow-sm"
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-(--bg-primary)">
          <div className="max-w-5xl mx-auto">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
